"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Loader2, RotateCcw, Search } from "lucide-react"
import { toast } from "sonner"

import { EditFiltersDialog } from "@/components/edit-filters-dialog"
import { FIELD_LABEL } from "@/components/graphite/field"
import { useRecentSearches } from "@/hooks/use-recent-searches"
import { useSearchReview } from "@/hooks/use-search-review"
import type { SearchFiltersDraft } from "@/lib/search-filter-model"
import { buildSearchPreflightSummary } from "@/lib/search-preflight-presenter"
import { cn } from "@/lib/utils"

/**
 * Starting a search, from canvas artboards 19e (wide) and 19f (narrow), with
 * 19k/19l on white.
 *
 * "The source builds this as a chat: rounded 32px bubbles, an assistant avatar,
 * example prompts as pills. Robin already has a thread on the left of every
 * screen, so a second fake conversation in the page is confusing about who you
 * are talking to. This is a form with one honest question: here is your prompt,
 * here is what it became, run it or fix it."
 *
 * The parsed filters read as one sentence with the machine's guesses underlined,
 * because an underline says "this is a span it decided" without turning the
 * sentence into a row of chips.
 */

interface NewJobComponentProps {
  onSearch: (query: string, filters: SearchFiltersDraft) => void
}

const MONO = "font-mono text-[11px] leading-none text-muted-foreground"

export function NewSearchComponent({ onSearch }: NewJobComponentProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const lastToastedErrorPromptRef = useRef("")
  const navigate = useNavigate()

  const trimmedQuery = searchQuery.trim()
  const review = useSearchReview({ query: trimmedQuery })
  const recent = useRecentSearches()

  const {
    canRunSearch,
    countError,
    estimatedCount,
    filters,
    isCounting,
    isManualCommittedForCurrentPrompt,
    isManualRecoveryAvailable,
    maxAttempts,
    parseError,
    resetFilters,
    setFiltersFromManualReview,
    setFiltersFromUser,
    startManualRecovery,
    status,
  } = review

  const isManualDialogMode = isManualRecoveryAvailable || isManualCommittedForCurrentPrompt
  const hasPrompt = Boolean(trimmedQuery)
  const parsedSentence = useMemo(() => buildSearchPreflightSummary(filters), [filters])

  useEffect(() => {
    if (!parseError) {
      lastToastedErrorPromptRef.current = ""
      return
    }
    if (lastToastedErrorPromptRef.current === parseError.prompt) return
    lastToastedErrorPromptRef.current = parseError.prompt
    toast.error(parseError.message)
  }, [parseError])

  /*
    19e writes "1,284 matches" beside the heading and "1,284 profiles" in the
    footer. It also writes "estimated in 0.4s" and "about 40 seconds"; neither
    has anything behind it — the count endpoint does not report its own timing
    and nothing predicts how long a run takes — so those are left out rather
    than invented.
  */
  const matchLabel = countError
    ? "estimate unavailable"
    : estimatedCount !== null
      ? `${estimatedCount.toLocaleString()} matches`
      : "estimating matches"

  const handleFiltersSubmit = (nextFilters: SearchFiltersDraft) => {
    if (isManualDialogMode) setFiltersFromManualReview(nextFilters)
    else setFiltersFromUser(nextFilters)
  }

  const handleReset = () => {
    setSearchQuery("")
    setIsFiltersOpen(false)
    resetFilters()
  }

  const handleSearch = () => {
    if (!trimmedQuery || !canRunSearch) return
    onSearch(trimmedQuery, filters)
  }

  const handleStartManualRecovery = () => {
    startManualRecovery()
    setIsFiltersOpen(true)
  }

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <header className="flex-none border-b px-5 pb-[18px] pt-5 @3xl:px-[26px]">
        <h1 className="text-[22px] font-semibold leading-[1.1] tracking-[-0.03em]">
          Find your next hire
        </h1>
        <p className="mt-[9px] max-w-[560px] text-[12.5px] leading-[1.6] text-muted-foreground">
          Describe who you want. Nothing runs until you have read what the filters
          became.
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-8 pt-5 @3xl:px-[26px]">
        <div className={FIELD_LABEL}>Your prompt</div>
        <textarea
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          rows={2}
          placeholder="Staff platform engineer in the EU, Kubernetes and Go, open to a move"
          aria-label="Your prompt"
          className="mt-[11px] w-full resize-none border-b bg-transparent pb-3 text-[15px] leading-[1.6] outline-none transition-colors placeholder:text-muted-foreground focus:border-primary @3xl:text-[17px]"
        />

        {hasPrompt ? (
          <section className="mt-[22px] border-t pt-[18px]">
            <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-2">
              <div className={FIELD_LABEL}>What that became</div>
              <span className="font-mono text-[11px] leading-none text-foreground">
                {matchLabel}
              </span>
              {isCounting && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}

              {status === "parse_error" ? (
                <button
                  type="button"
                  onClick={handleStartManualRecovery}
                  className="ml-auto text-[12px] font-medium text-brand-ink transition-opacity hover:opacity-80"
                >
                  Set filters by hand
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsFiltersOpen(true)}
                  className="ml-auto text-[12px] font-medium text-brand-ink transition-opacity hover:opacity-80"
                >
                  Edit filters
                </button>
              )}
            </div>

            {status === "parse_error" ? (
              <p className="mt-3.5 text-[13.5px] leading-[1.8] @3xl:text-[15px] @3xl:leading-[1.9]">
                That prompt could not be turned into filters after {maxAttempts}{" "}
                attempts.{" "}
                <span className="text-muted-foreground">
                  {parseError?.message ?? "Set them by hand to carry on."}
                </span>
              </p>
            ) : (
              <p className="mt-3.5 text-[13.5px] leading-[1.8] @3xl:text-[15px] @3xl:leading-[1.9]">
                {parsedSentence.map((part, index) =>
                  part.tone === "highlight" ? (
                    /*
                      Underlined, not a chip. The sentence has to stay a sentence
                      for the guesses inside it to be readable as guesses.
                    */
                    <span
                      key={`${part.text}-${index}`}
                      className="border-b-[1.5px] border-primary pb-px"
                    >
                      {part.text}
                    </span>
                  ) : (
                    <span key={`${part.text}-${index}`} className="whitespace-pre-wrap">
                      {part.text}
                    </span>
                  ),
                )}
              </p>
            )}

            {status !== "parse_error" && (
              <p className="mt-3.5 text-[11px] leading-[1.55] text-muted-foreground">
                Underlined spans are the filters — what your sentence was read as.
                Worth checking before{" "}
                {estimatedCount !== null
                  ? `${estimatedCount.toLocaleString()} profiles get pulled`
                  : "the profiles get pulled"}
                .
              </p>
            )}

            {countError && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                The match estimate is unavailable, but the search can still run.
              </p>
            )}
          </section>
        ) : null}

        <section className="mt-[22px]">
          <div className="flex items-baseline gap-3.5">
            <div className={FIELD_LABEL}>Searches like this</div>
            {/* The caption crowds the label on a phone, where 19f drops it too. */}
            <span className={cn(MONO, "ml-auto hidden @xl:inline")}>
              your 20 most recent, by last run
            </span>
          </div>

          <div className="mt-2">
            {recent.isLoading ? (
              <p className="flex items-center gap-2 border-t py-[11px] text-[12.5px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading your searches…
              </p>
            ) : recent.searches.length === 0 ? (
              <p className="border-t py-[11px] text-[12.5px] text-muted-foreground">
                Nothing yet. The first search you run will be here.
              </p>
            ) : (
              recent.searches.map((search) => (
                <button
                  key={search.hash}
                  type="button"
                  onClick={() =>
                    navigate({
                      to: "/searches/$searchId/results",
                      params: { searchId: search.hash },
                      search: { page: 1 },
                    })
                  }
                  className="flex w-full items-baseline gap-3.5 border-t py-[11px] text-left transition-opacity hover:opacity-70"
                >
                  <span className="min-w-0 flex-1 truncate text-[12.5px] leading-[1.4]">
                    {search.prompt}
                  </span>
                  {search.results !== undefined && (
                    <span className={cn(MONO, "whitespace-nowrap")}>
                      {search.results.toLocaleString()} results
                    </span>
                  )}
                  <span className={cn(MONO, "hidden w-[112px] flex-none text-right @xl:block")}>
                    {search.when}
                  </span>
                </button>
              ))
            )}
          </div>
        </section>
      </div>

      <footer className="flex flex-none items-center gap-5 border-t bg-chrome px-5 py-4 @3xl:px-[26px]">
        <button
          type="button"
          onClick={handleSearch}
          disabled={!canRunSearch}
          className="inline-flex items-center gap-2 text-[13px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Search className="h-[15px] w-[15px]" />
          Run the search
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!hasPrompt}
          className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </button>
        {estimatedCount !== null && (
          <span className={cn(MONO, "ml-auto")}>
            {estimatedCount.toLocaleString()} profiles
          </span>
        )}
      </footer>

      <EditFiltersDialog
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        initialFilters={filters}
        onSubmit={handleFiltersSubmit}
        searchPrompt={trimmedQuery}
        title={isManualDialogMode ? "Set filters manually" : "Review parsed filters"}
        description={
          isManualDialogMode
            ? "This prompt could not be parsed automatically. Set the filters by hand to continue."
            : "Adjust what your sentence was read as before the search runs."
        }
        submitLabel={isManualDialogMode ? "Apply manual filters" : "Apply filters"}
      />
    </div>
  )
}
